import React, {useState} from 'react'
import {IAP_METADATA_COLUMN_DEFINITIONS, IOG_METADATA_COLUMN_DEFINITIONS} from './MetadataTableProperty'
import {NonCancelableCustomEvent, PaginationProps, PropertyFilterProps} from '@amzn/awsui-components-react'
import {LazyLoadPaginationProps, TableDetails, TableInput, useTableCreation} from '../TableTemplate'
import {MetadataQuery} from '../../backend/model'

export type MetadataType = 'IOG' | 'IAP'

export interface MetadataTableInput extends TableInput {
    metadataType: MetadataType,
    fetchDataHandler: (query: MetadataQuery) => void,
    advancedSearchPayload: MetadataQuery,
}

const SPECIAL_KEYS = ['iogId', 'iapId']

const METADATA_GET_ROW_FUNCTION: { [metadataType in MetadataType]: (row: any) => string[] } = {
    'IOG': (row: any) => {
        const {iogId, ...rest} = row
        return [iogId, ...Object.values(rest)]
    },
    'IAP': (row: any) => {
        const {iapId, ...rest} = row
        return [iapId, ...Object.values(rest)]
    },
}

export const MetaDataTable: React.FC<MetadataTableInput> = (
    {
        tableItems,
        fileName,
        metadataType,
        fetchDataHandler,
        advancedSearchPayload,
    }) => {
    const [currentPageNumber, setCurrentPageNumber] = useState(1)
    const [isLoading, setIsLoading] = useState(false)
    const dataRemaining = advancedSearchPayload.nextToken != null

    const convertKeyToLabel = (key: string): string => {
        return key.split('_').map((str) =>
            str.charAt(0).toUpperCase() + str.slice(1),
        ).join(' ')
    }

    function getFilteringProperties(): PropertyFilterProps.FilteringProperty[] {
        const filterProperties: PropertyFilterProps.FilteringProperty[] = []

        if (tableItems && tableItems.length > 0) {
            const item = tableItems[0]
            for (const key in item) {
                const label = convertKeyToLabel(key)
                filterProperties.push(
                    {
                        key: key,
                        operators: ['=', '!='],
                        propertyLabel: label,
                        groupValuesLabel: `${label}s`,
                    },
                )
            }
        }

        return filterProperties
    }

    function getColumnDefinition(): any[] {
        const columnDefinition = metadataType === 'IOG' ? [...IOG_METADATA_COLUMN_DEFINITIONS] : [...IAP_METADATA_COLUMN_DEFINITIONS]

        if (tableItems && tableItems.length > 0) {
            const item = tableItems[0]
            for (const key in item) {
                if (!SPECIAL_KEYS.includes(key)) {
                    const label = convertKeyToLabel(key)
                    columnDefinition.push(
                        {
                            id: key,
                            header: label,
                            cell: (value: any): string => value[key],
                        },
                    )
                }
            }
        }

        return columnDefinition
    }

    const metadataFilteringProperty = getFilteringProperties()
    const metadataColumnDefinitions = getColumnDefinition()

    const tableDetails: TableDetails = {
        tableId: `${metadataType.toLowerCase()}MetadataTable`,
        header: `${metadataType} Metadata Query Results`,
        fileName: fileName,
        getRow: METADATA_GET_ROW_FUNCTION[metadataType],
    }

    const handleNexPageClick = async (
        {detail: {requestedPageAvailable, requestedPageIndex}}: NonCancelableCustomEvent<PaginationProps.PageClickDetail>,
    ) => {
        if (isLoading) return

        if (!requestedPageAvailable && advancedSearchPayload.nextToken) {
            setIsLoading(true)
            await fetchDataHandler(advancedSearchPayload)
            setIsLoading(false)
        }

        setCurrentPageNumber(requestedPageIndex)
    }

    const totalPages = tableItems ? Math.ceil(tableItems.length / 10) : 0

    const lazyLoadPaginationProps: LazyLoadPaginationProps = {
        currentPageIndex: currentPageNumber,
        onNextPageClick: handleNexPageClick,
        pagesCount: totalPages,
        disabled: isLoading,
        openEnd: dataRemaining,
    }

    return useTableCreation(tableDetails, metadataColumnDefinitions, tableItems, metadataFilteringProperty, undefined, lazyLoadPaginationProps)
}
